import { getReservationModeLabels } from '@iheartjane/menu';
import type { ReservationMode } from '@iheartjane/types';
import { useMemo } from 'react';

import {
  getOrderingSchedule,
  isAcceptingOrders,
} from '@jane/shared-ecomm/util';
import type { StoreSchedule } from '@jane/shared-ecomm/util';
import { useReservationSlots } from '@jane/shared/data-access';
// eslint-disable-next-line no-restricted-imports
import type { Address, Store } from '@jane/shared/models';
import type { Store as zStore } from '@jane/shared/types';

interface UseReservationAvailability {
  address: Address;
  reservationMode?: ReservationMode;
  store: Store | zStore;
}
interface ReservationAvailability {
  canGoToCheckout: boolean;
  checkoutButtonLabel: string;
  deliveryLabel: string;
  deliverySchedule: StoreSchedule;
  hasDeliverySlots: boolean;
  hasPickupSlots: boolean;
  isAcceptingDeliveryOrders: boolean;
  isLoading: boolean;
  modesToRender: ReservationMode[];
  pickupLabel: string;
  pickupSchedule: StoreSchedule;
  refetchDelivery: () => void;
  refetchPickup: () => void;
}

export const useReservationAvailability = ({
  address,
  reservationMode,
  store,
}: UseReservationAvailability): ReservationAvailability => {
  const hasAddress = !!address?.lat && !!address?.lng && !!address?.zipcode;

  /**
   * If there is no address, we can't get delivery slots
   * this check keeps the tab from being disabled
   */
  const isAcceptingDeliveryOrders = useMemo(
    () => isAcceptingOrders(store as zStore, 'delivery'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      store.time_zone_identifier,
      store.delivery_hours,
      store.allow_off_hours_ordering,
      store.allow_future_day_ordering,
    ]
  );

  const { deliverySchedule, pickupSchedule } = useMemo(
    () =>
      getOrderingSchedule({
        store,
        type: ['delivery', 'pickup'],
      }),
    [store]
  );

  const isPickupOpen = pickupSchedule
    ? !pickupSchedule?.isAlwaysClosed()
    : false;
  const isDeliveryOpen = deliverySchedule
    ? !deliverySchedule?.isAlwaysClosed()
    : false;

  const {
    data: pickupSlots,
    isFetching: pickupFetching,
    refetch: refetchPickup,
  } = useReservationSlots(
    {
      reservationMode: 'pickup',
      storeId: store?.id,
    },
    { enabled: isPickupOpen }
  );

  const {
    data: deliverySlots,
    isFetching: deliveryFetching,
    refetch: refetchDelivery,
  } = useReservationSlots(
    {
      reservationMode: 'delivery',
      storeId: store?.id,
      lat: address?.lat,
      lng: address?.lng,
      zipcode: address?.zipcode,
    },
    {
      enabled: isDeliveryOpen,
    }
  );

  return useMemo(() => {
    const reservationModes: ReservationMode[] = ['delivery', 'pickup'];

    const modesToRender = reservationModes.filter(
      (mode) =>
        (mode === 'delivery' && isDeliveryOpen) ||
        (mode === 'pickup' && isPickupOpen)
    );

    const { pickupLabel, deliveryLabel } = getReservationModeLabels(
      (store as Store).reservation_mode_labels
    );

    const hasPickupSlots = pickupFetching
      ? true
      : (pickupSlots?.reservation_slots?.length ?? 0) > 0;
    const hasDeliverySlots = deliveryFetching
      ? true
      : (deliverySlots?.reservation_slots?.length ?? 0) > 0;

    const hasSelectedDelivery = reservationMode
      ? reservationMode === 'delivery'
      : false;
    const missingAddress = !!hasSelectedDelivery && !hasAddress;

    return {
      canGoToCheckout: hasSelectedDelivery
        ? hasAddress && hasDeliverySlots
        : hasPickupSlots,
      checkoutButtonLabel: missingAddress
        ? 'Please enter your address'
        : 'Proceed to checkout',
      deliveryLabel,
      deliverySchedule,
      hasPickupSlots,
      hasDeliverySlots: !hasAddress ? true : hasDeliverySlots,
      isAcceptingDeliveryOrders,
      isLoading: hasSelectedDelivery ? deliveryFetching : pickupFetching,
      modesToRender,
      pickupLabel,
      pickupSchedule,
      refetchPickup,
      refetchDelivery,
    };
  }, [
    deliveryFetching,
    deliverySchedule,
    deliverySlots?.reservation_slots?.length,
    hasAddress,
    isAcceptingDeliveryOrders,
    isDeliveryOpen,
    isPickupOpen,
    pickupFetching,
    pickupSchedule,
    pickupSlots?.reservation_slots?.length,
    refetchDelivery,
    refetchPickup,
    reservationMode,
    store,
  ]);
};
